import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserAvatar from "@/components/UserAvatar";
import { toast } from "sonner";
import {
  User, Heart, Sparkle, TShirt, CreditCard, WhatsappLogo, CheckCircle, ArrowLeft, ArrowRight,
  Download, QrCode, Trophy, ChartLine, Package, CurrencyDollar, ShieldStar, MagnifyingGlass, X,
  UserPlus, PencilSimple, Users, Trash, ArrowsClockwise, Camera, FilePdf, UploadSimple, Plus,
} from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITIONS = ["Asma","Hipertensión","Diabetes","Alergias","Lesión articular","Otra","Ninguna"];
const BLOODS = ["O+","O-","A+","A-","B+","B-","AB+","AB-","No lo sé"];
const RELATIONS = ["Mamá","Papá","Pareja","Amiga","Amigo","Hijo/a","Hermano/a","Otro"];
const FREQUENCIES = ["1-2","3-4","5 o más","Estoy empezando, es mi primera vez"];
const PREVIOUS = ["Sí, 5K","Sí, 10K o más","No, esta es mi primera carrera"];
const OBJECTIVES = ["Terminar y divertirme","Mejorar mi tiempo","Mi primera carrera con familia/amigos"];
const SIZES = ["XS","S","M","L","XL","XXL"];
const GENDERS = [
  { value: "F", label: "Mujer" },
  { value: "M", label: "Hombre" },
];

const SIZE_GUIDE = [
  { size: "XS", busto: "78-82", largo: "60" },
  { size: "S",  busto: "83-87", largo: "62" },
  { size: "M",  busto: "88-92", largo: "64" },
  { size: "L",  busto: "93-97", largo: "66" },
  { size: "XL", busto: "98-104", largo: "68" },
];

// ============================================================
// LIST PAGE (route: /dashboard/dyd-run/carreras)
// ============================================================
export function RacesList() {
  const { user } = useAuth();
  const [races, setRaces] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [brandCreateOpen, setBrandCreateOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null); // id of brand drilled in
  const [editingBrand, setEditingBrand] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const navigate = useNavigate();
  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/races"),
      api.get("/race-brands"),
    ]).then(([rRes, bRes]) => {
      setRaces(rRes.data || []);
      setBrands(bRes.data?.brands || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const isAdmin = user?.role === "admin";

  const deleteRace = async (race) => {
    try {
      await api.delete(`/races/${race.id}`);
      toast.success(`"${race.name}" eliminada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const archiveBrand = async (brand) => {
    if (!window.confirm(`¿Archivar la marca "${brand.name}"? Los eventos asociados conservarán sus datos pero la marca dejará de aparecer en el grid.`)) return;
    try {
      await api.delete(`/race-brands/${brand.id}`);
      toast.success(`Marca "${brand.name}" archivada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo archivar");
    }
  };

  // Drag & drop reorder — persists via PATCH sort on each brand after drop
  const dragPropsFor = (brand) => ({
    dragging: draggingId === brand.id,
    dragOver: dragOverId === brand.id && draggingId !== brand.id,
    onDragStart: (e) => { setDraggingId(brand.id); try { e.dataTransfer.setData("text/plain", brand.id); e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ } },
    onDragEnd: () => { setDraggingId(null); setDragOverId(null); },
    onDragOver: (e) => { if (draggingId && draggingId !== brand.id) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverId(brand.id); } },
    onDragLeave: () => { setDragOverId((cur) => (cur === brand.id ? null : cur)); },
    onDrop: async (e) => {
      e.preventDefault();
      const srcId = draggingId || e.dataTransfer.getData("text/plain");
      setDraggingId(null); setDragOverId(null);
      if (!srcId || srcId === brand.id) return;
      // Reorder the real brands array (exclude __none__ bucket if present).
      const real = brands.filter((b) => b.id !== "__none__");
      const srcIdx = real.findIndex((b) => b.id === srcId);
      const dstIdx = real.findIndex((b) => b.id === brand.id);
      if (srcIdx < 0 || dstIdx < 0) return;
      const next = [...real];
      const [moved] = next.splice(srcIdx, 1);
      next.splice(dstIdx, 0, moved);
      // Optimistic update
      const withSort = next.map((b, i) => ({ ...b, sort: (i + 1) * 10 }));
      setBrands(withSort);
      // Persist each affected sort value
      try {
        await Promise.all(withSort.map((b) => api.patch(`/race-brands/${b.id}`, { sort: b.sort })));
        toast.success("Orden guardado");
      } catch (err) {
        toast.error("No se pudo guardar el orden");
        load();
      }
    },
  });

  // Group races by brand_id, with "sin-marca" bucket for unassigned
  const unassignedRaces = races.filter((r) => !r.brand_id);
  const selectedBrandObj = brands.find((b) => b.id === selectedBrand);
  const brandRaces = selectedBrand === "__none__"
    ? unassignedRaces
    : races.filter((r) => r.brand_id === selectedBrand);

  return (
    <div className="space-y-4" data-testid="races-list-page">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold">DYD Run · {selectedBrand ? "Eventos" : "Marcas organizadoras"}</p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tighter">
            {selectedBrand ? (selectedBrandObj?.name || "Sin marca") : "Carreras"}
          </h1>
          {selectedBrandObj?.tagline && <p className="text-sm text-gray-400 mt-1">{selectedBrandObj.tagline}</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectedBrand && (
            <Button variant="outline" onClick={() => setSelectedBrand(null)} className="border-white/20 text-white hover:bg-white/5" data-testid="races-back-to-brands">
              ← Marcas
            </Button>
          )}
          {isAdmin && selectedBrand && selectedBrandObj && (
            <Button variant="outline" onClick={() => setEditingBrand(selectedBrandObj)} className="border-[#CCFF00]/50 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase" data-testid="brand-edit-from-header">
              <PencilSimple size={14} className="mr-1" /> Editar marca
            </Button>
          )}
          {isAdmin && !selectedBrand && (
            <Button onClick={() => setBrandCreateOpen(true)} variant="outline" className="border-[#CCFF00]/50 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase" data-testid="brand-create-btn">
              + Marca
            </Button>
          )}
          {isAdmin && selectedBrand && (
            <Button onClick={() => setCreateOpen(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="race-create-btn">
              <UserPlus size={14} className="mr-1" /> Crear evento
            </Button>
          )}
        </div>
      </header>

      {loading ? <div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" /> : (
        !selectedBrand ? (
          // ===== VIEW 1: Grid de marcas =====
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {brands.map((b) => (
              <BrandCard
                key={b.id}
                brand={b}
                onClick={() => setSelectedBrand(b.id)}
                isAdmin={isAdmin}
                onArchive={archiveBrand}
                onEdit={setEditingBrand}
                dragProps={isAdmin && b.id !== "__none__" ? dragPropsFor(b) : {}}
              />
            ))}
            {unassignedRaces.length > 0 && (
              <BrandCard
                brand={{ id: "__none__", name: "Eventos sin marca", tagline: "Carreras independientes", events_count: unassignedRaces.length, primary_color: "#333", accent_color: "#CCFF00" }}
                onClick={() => setSelectedBrand("__none__")}
              />
            )}
            {brands.length === 0 && unassignedRaces.length === 0 && (
              <div className="col-span-full text-center p-10 border border-dashed border-white/10 rounded-xl">
                <p className="text-white/60">Aún no hay marcas ni eventos.</p>
              </div>
            )}
          </div>
        ) : (
          // ===== VIEW 2: Eventos de la marca =====
          <div className="grid gap-4 sm:grid-cols-2">
            {brandRaces.length === 0 ? (
              <div className="col-span-full text-center p-10 border border-dashed border-white/10 rounded-xl">
                <p className="text-white/60">Esta marca aún no tiene eventos.</p>
              </div>
            ) : brandRaces.map((r) => <RaceCard key={r.id} race={r} onClick={() => navigate(`/dashboard/dyd-run/carreras/${r.id}`)} isAdmin={isAdmin} onDelete={deleteRace} />)}
          </div>
        )
      )}

      {isAdmin && (
        <>
          <RaceEditorDialog
            mode="create"
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            defaultBrandId={selectedBrand && selectedBrand !== "__none__" ? selectedBrand : null}
            onSaved={() => { setCreateOpen(false); load(); }}
          />
          <BrandEditorDialog
            open={brandCreateOpen}
            onClose={() => setBrandCreateOpen(false)}
            onSaved={() => { setBrandCreateOpen(false); load(); }}
          />
          <BrandEditorDialog
            open={!!editingBrand}
            brand={editingBrand}
            onClose={() => setEditingBrand(null)}
            onSaved={() => { load(); }}
          />
        </>
      )}
    </div>
  );
}

function BrandCard({ brand, onClick, isAdmin = false, onArchive, onEdit, dragProps = {} }) {
  const bg = `linear-gradient(135deg, ${brand.primary_color || "#5B1A2A"} 0%, ${brand.accent_color || "#B85A6F"} 100%)`;
  const handleArchive = (e) => {
    e.stopPropagation();
    onArchive?.(brand);
  };
  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(brand);
  };
  const logoSrc = brand.logo_url ? `${BACKEND_URL}${brand.logo_url}` : null;
  return (
    <div
      role="button"
      onClick={onClick}
      className={`relative text-left rounded-xl overflow-hidden border border-white/10 hover:brightness-110 hover:scale-[1.02] transition-all shadow-xl cursor-pointer ${dragProps.dragging ? "opacity-40" : ""} ${dragProps.dragOver ? "ring-2 ring-[#CCFF00]" : ""}`}
      style={{ background: bg, minHeight: 160 }}
      data-testid={`brand-card-${brand.id}`}
      draggable={isAdmin && brand.id !== "__none__"}
      onDragStart={dragProps.onDragStart}
      onDragEnd={dragProps.onDragEnd}
      onDragOver={dragProps.onDragOver}
      onDragLeave={dragProps.onDragLeave}
      onDrop={dragProps.onDrop}
    >
      <div className="p-5 text-white flex items-start gap-3">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={`${brand.name} logo`}
            className="w-14 h-14 rounded-lg object-contain bg-white/95 p-1 shrink-0 shadow"
            data-testid={`brand-card-${brand.id}-logo`}
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-white/20 flex items-center justify-center text-white font-black text-2xl shrink-0 border border-white/30">
            {(brand.name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Marca</p>
          <h3 className="font-['Barlow_Condensed'] text-2xl font-black leading-tight mt-0.5 truncate">{brand.name}</h3>
          {brand.tagline && <p className="text-xs opacity-90 mt-1 line-clamp-2">{brand.tagline}</p>}
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-black/30 border border-white/20 text-white text-[10px]">
              {brand.events_count ?? 0} evento{(brand.events_count ?? 0) === 1 ? "" : "s"}
            </Badge>
          </div>
        </div>
      </div>
      <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/90 text-black flex items-center justify-center font-black text-lg">›</span>
      {isAdmin && brand.id !== "__none__" && (
        <div className="absolute top-2 right-2 flex gap-1.5">
          <button
            type="button"
            onClick={handleEdit}
            className="w-9 h-9 rounded-full bg-black/70 hover:bg-[#CCFF00] hover:text-black text-white flex items-center justify-center shadow-lg transition-colors"
            aria-label={`Editar ${brand.name}`}
            data-testid={`brand-card-${brand.id}-edit`}
          >
            <PencilSimple size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={handleArchive}
            className="w-9 h-9 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
            aria-label={`Archivar ${brand.name}`}
            data-testid={`brand-card-${brand.id}-archive`}
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      )}
      {isAdmin && brand.id !== "__none__" && (
        <div className="absolute top-2 left-2 w-6 h-6 rounded-md bg-black/40 text-white/70 flex items-center justify-center text-[10px] font-bold select-none" title="Arrastra para reordenar">
          ⋮⋮
        </div>
      )}
    </div>
  );
}

function BrandEditorDialog({ open, onClose, onSaved, brand = null }) {
  const isEdit = !!brand;
  const EMPTY = { name: "", tagline: "", description: "", primary_color: "#5B1A2A", accent_color: "#B85A6F", is_active: true };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    if (!open) { setForm(EMPTY); setLogoPreview(null); return; }
    if (brand) {
      setForm({
        name: brand.name || "",
        tagline: brand.tagline || "",
        description: brand.description || "",
        primary_color: brand.primary_color || "#5B1A2A",
        accent_color: brand.accent_color || "#B85A6F",
        is_active: brand.is_active !== false,
      });
      setLogoPreview(brand.logo_url ? `${BACKEND_URL}${brand.logo_url}?t=${Date.now()}` : null);
    } else {
      setForm(EMPTY);
      setLogoPreview(null);
    }
    // eslint-disable-next-line
  }, [open, brand?.id]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/race-brands/${brand.id}`, form);
        toast.success("Marca actualizada");
      } else {
        await api.post("/race-brands", form);
        toast.success("Marca creada");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file || !isEdit) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El logo supera 5MB"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/race-brands/${brand.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPreview(URL.createObjectURL(file));
      toast.success("Logo subido");
      onSaved?.(); // refresh list
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo subir el logo");
    } finally { setUploadingLogo(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white" data-testid="brand-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">{isEdit ? `Editar ${brand.name}` : "Nueva marca"}</DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            {isEdit ? "Edita el perfil de la marca. El logo se guarda al momento." : "Las marcas agrupan eventos (ej: Piste & Pace → VaqueRun + CorreWeen)."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase text-white/70">Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Piste & Pace" className="bg-black/40 border-white/20" data-testid="brand-form-name" /></div>
          <div><Label className="text-xs uppercase text-white/70">Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Corre con sabor a Yucatán" className="bg-black/40 border-white/20" data-testid="brand-form-tagline" /></div>
          <div><Label className="text-xs uppercase text-white/70">Descripción</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/40 border-white/20" data-testid="brand-form-desc" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs uppercase text-white/70">Color primario</Label><Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="bg-black/40 border-white/20 h-11" data-testid="brand-form-primary" /></div>
            <div><Label className="text-xs uppercase text-white/70">Color acento</Label><Input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="bg-black/40 border-white/20 h-11" data-testid="brand-form-accent" /></div>
          </div>
          {/* Preview */}
          <div className="rounded-lg p-3 border border-white/10" style={{ background: `linear-gradient(135deg, ${form.primary_color} 0%, ${form.accent_color} 100%)` }}>
            <div className="flex items-center gap-3">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="w-12 h-12 rounded-lg object-cover bg-white/20 shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center text-white font-black text-xl shrink-0">
                  {(form.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white leading-tight truncate">{form.name || "Nombre de marca"}</p>
                <p className="text-xs text-white/80 truncate">{form.tagline || "Tagline aparece aquí"}</p>
              </div>
            </div>
          </div>
          {isEdit && (
            <div>
              <Label className="text-xs uppercase text-white/70">Logo (PNG/JPG/WebP · máx 5MB)</Label>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                disabled={uploadingLogo}
                className="block w-full text-sm text-white/80 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-bold file:bg-[#CCFF00] file:text-black hover:file:bg-[#B3E600] mt-1"
                data-testid="brand-form-logo"
              />
            </div>
          )}
          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="brand-form-active" />
              <span className="text-xs uppercase tracking-widest text-white/80">Marca activa</span>
            </label>
          )}
          <Button onClick={submit} disabled={saving} className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="brand-form-save">
            {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear marca")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RaceCard({ race, onClick, isAdmin = false, onDelete }) {
  const t = race.theme || {};
  const handleDelete = (e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la carrera "${race.name}"? Esta acción no se puede deshacer y las inscripciones quedarán huérfanas.`)) return;
    onDelete?.(race);
  };
  return (
    <div
      className="relative text-left rounded-lg overflow-hidden border-2 hover:brightness-110 transition-all cursor-pointer"
      style={{ background: t.primary_soft || "#F4D5DA", borderColor: t.primary || "#5B1A2A" }}
      data-testid={`race-card-${race.id}`}
      role="button"
      onClick={onClick}
    >
      <div className="p-5" style={{ color: t.primary || "#5B1A2A" }}>
        <p className="text-[10px] uppercase tracking-widest opacity-70">{race.brand}</p>
        <h3 className="font-['Barlow_Condensed'] text-4xl font-black leading-none mt-1">{race.name}</h3>
        <p className="text-sm mt-2">{race.subtitle}</p>
        <div className="flex items-center gap-3 mt-3 text-xs">
          <Badge className="bg-white/70" style={{ color: t.primary }}>{race.event_date}</Badge>
          <Badge className="bg-white/70" style={{ color: t.primary }}>${race.kit_price_mxn} {race.currency}</Badge>
        </div>
      </div>
      {isAdmin && (
        <button
          type="button"
          onClick={handleDelete}
          className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-colors"
          aria-label={`Eliminar ${race.name}`}
          data-testid={`race-card-${race.id}-delete`}
        >
          <X size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}

// ============================================================
// RACE DETAIL + REGISTRATION WIZARD (route: /dashboard/dyd-run/carreras/:raceId)
// ============================================================
export default function RaceDetail() {
  const { raceId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [race, setRace] = useState(null);
  const [myReg, setMyReg] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    full_name: user?.name || "", whatsapp: user?.phone || "", email: user?.email || "",
    password: "", password2: "", privacy_accepted: false,
    gender: "F",
    age: "", blood_type: "", conditions: [], conditions_detail: "",
    medications: false, medications_detail: "",
    emergency_name: "", emergency_phone: "", emergency_relation: "",
    sport_frequency: "", previous_race: "", objective: "", shirt_size: "",
    payment_method: "stripe",
    waiver_accepted: false,
  });
  const [saving, setSaving] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [managersOpen, setManagersOpen] = useState(false);
  const [myAccess, setMyAccess] = useState({ is_manager: false, permission_level: null, is_admin: false });
  const [sombreroStatus, setSombreroStatus] = useState(null);

  const sessionId = searchParams.get("session_id");
  const t = race?.theme || {};
  const isManager = myAccess.is_manager || user?.role === "admin";

  useEffect(() => {
    api.get(`/races/${raceId}`).then(({ data }) => setRace(data)).catch(() => toast.error("Carrera no encontrada"));
    api.get(`/races/${raceId}/my-registration`).then(({ data }) => { setMyReg(data); setStep(5); }).catch(() => {});
    api.get(`/races/${raceId}/my-access`).then(({ data }) => setMyAccess(data)).catch(() => {});
    api.get(`/races/${raceId}/sombrero-status`).then(({ data }) => setSombreroStatus(data)).catch(() => {});
  }, [raceId]);

  // Handle Stripe return
  useEffect(() => {
    if (!sessionId || !myReg) return;
    api.get(`/races/${raceId}/registrations/${myReg.id}/verify-stripe?session_id=${sessionId}`)
      .then(({ data }) => { if (data.paid) { toast.success("¡Pago confirmado!"); setMyReg(data.registration); } })
      .catch(() => {});
  }, [sessionId, myReg, raceId]);

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.full_name.trim()) return "Nombre completo requerido";
      if (!form.gender) return "Selecciona género";
      if (!/^\+?\d[\d\s-]{7,}$/.test(form.whatsapp)) return "WhatsApp inválido";
      if (!/@/.test(form.email)) return "Correo inválido";
      if (form.password.length < 8) return "Contraseña mínimo 8 caracteres";
      if (form.password !== form.password2) return "Las contraseñas no coinciden";
      if (!form.privacy_accepted) return "Acepta el aviso de privacidad";
    }
    if (step === 4) {
      if (!form.waiver_accepted) return "Debes aceptar el deslinde de responsabilidad médica";
    }
    if (step === 2) {
      if (!form.age || form.age < 10) return "Edad requerida";
      if (!form.blood_type) return "Tipo de sangre requerido";
      if (!form.emergency_name.trim()) return "Contacto de emergencia requerido";
      if (!form.emergency_phone.trim()) return "Teléfono de emergencia requerido";
      if (!form.emergency_relation) return "Parentesco requerido";
    }
    if (step === 3) {
      if (!form.sport_frequency) return "Frecuencia deportiva requerida";
      if (!form.previous_race) return "Experiencia previa requerida";
      if (!form.objective) return "Objetivo requerido";
      if (!form.shirt_size) return "Talla requerida";
    }
    return null;
  };

  const nextStep = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitRegistration = async (method) => {
    setSaving(true);
    try {
      const payload = { ...form, payment_method: method, step_completed: 4, age: Number(form.age) || 0 };
      const { data } = await api.post(`/races/${raceId}/register`, payload);
      setMyReg(data.registration);
      if (method === "stripe") {
        const { data: pay } = await api.post(`/races/${raceId}/registrations/${data.registration.id}/pay/stripe`, { origin: window.location.origin });
        window.location.href = pay.checkout_url;
      } else {
        await api.post(`/races/${raceId}/registrations/${data.registration.id}/pay/transfer`);
        toast.success("Registro creado. El admin verificará tu transferencia.");
        setStep(5);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };

  if (!race) return <div className="p-8 text-center text-gray-400">Cargando…</div>;

  const themeStyle = { "--r-primary": t.primary, "--r-soft": t.primary_soft, "--r-accent": t.accent };
  const primaryBg = { background: t.primary };
  const primaryText = { color: t.primary };

  return (
    <div className="min-h-full pb-8" style={themeStyle} data-testid="race-detail-page">
      {/* Header */}
      <div className="rounded-lg overflow-hidden mb-4" style={{ background: t.primary_soft }}>
        <div className="p-5 flex items-start justify-between flex-wrap gap-2">
          <div style={{ color: t.primary }}>
            <p className="text-[10px] uppercase tracking-widest opacity-70">{race.brand}</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black leading-none">{race.name}</h1>
            <p className="text-sm mt-2">{race.subtitle} · {race.location} · {race.event_date}</p>
          </div>
          {isManager && (
            <div className="flex gap-2">
              <Button onClick={() => navigate(`/dashboard/dyd-run/carreras/${raceId}/admin`)} className="text-white font-bold uppercase" style={primaryBg} data-testid="race-open-admin">
                <ChartLine size={14} className="mr-1" /> Dashboard admin
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Success / My registration */}
      {myReg && step >= 5 ? (
        <SuccessCard race={race} reg={myReg} onOpenManagers={() => setManagersOpen(true)} />
      ) : (
        <div className="max-w-lg mx-auto bg-white rounded-lg p-4 sm:p-5 shadow-sm text-black">
          <StepIndicator step={step} theme={t} />
          {step === 1 && <Step1 form={form} u={u} theme={t} race={race} />}
          {step === 2 && <Step2 form={form} u={u} theme={t} />}
          {step === 3 && <Step3 form={form} u={u} theme={t} onSizeGuide={() => setSizeGuideOpen(true)} />}
          {step === 4 && <Step4 race={race} form={form} u={u} theme={t} onSubmit={submitRegistration} saving={saving} sombreroStatus={sombreroStatus} />}

          {step < 4 && (
            <div className="flex justify-between mt-6">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="text-xs uppercase" data-testid="race-back">
                  <ArrowLeft size={14} className="mr-1" /> Atrás
                </Button>
              )}
              <Button onClick={nextStep} className="ml-auto text-white font-bold uppercase" style={primaryBg} data-testid="race-next">
                Continuar <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          )}
        </div>
      )}

      <SizeGuideDialog open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} theme={t} race={race} />
    </div>
  );
}

function StepIndicator({ step, theme }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="race-step-indicator">
      {[1,2,3,4].map((n) => (
        <div key={n} className="flex-1 h-1.5 rounded-full" style={{ background: n <= step ? (theme.primary || "#5B1A2A") : "rgba(0,0,0,0.15)" }} />
      ))}
    </div>
  );
}

function Step1({ form, u, theme, race }) {
  const brand = race?.brand || "";
  const welcomeTitle = (race?.welcome_enabled && race?.welcome_title) || `Únete a ${race?.name || "la carrera"}`;
  return (
    <section data-testid="race-step-1" className="space-y-3 text-black">
      <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-black">{welcomeTitle}</h2>
      {race?.welcome_enabled && race?.welcome_message && (
        <div className="p-3 rounded-lg border-2 bg-white" style={{ borderColor: theme.primary }} data-testid="race-welcome-hero">
          <p className="text-xs uppercase tracking-widest font-bold mb-1" style={{ color: theme.primary }}>Bienvenida</p>
          <p className="text-sm text-black">{race.welcome_message}</p>
        </div>
      )}
      <p className="text-sm text-black/80">Crea tu cuenta para guardar tu número y tu bib.</p>
      <Field label="Nombre completo *"><Input value={form.full_name} onChange={(e) => u("full_name", e.target.value)} className="text-black bg-white" data-testid="race-name" /></Field>
      <Field label="Género *">
        <div className="flex gap-2" data-testid="race-gender-picker">
          {GENDERS.map((g) => (
            <button key={g.value} type="button" onClick={() => u("gender", g.value)}
              className={`flex-1 py-3 rounded-lg border-2 font-bold ${form.gender === g.value ? "text-white" : "bg-white text-black"}`}
              style={form.gender === g.value ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }}
              data-testid={`race-gender-${g.value}`}>
              {g.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label="WhatsApp *" hint="Te mandaremos tu bib aquí"><Input type="tel" value={form.whatsapp} onChange={(e) => u("whatsapp", e.target.value)} className="text-black bg-white" data-testid="race-whatsapp" /></Field>
      <Field label="Correo electrónico *"><Input type="email" value={form.email} onChange={(e) => u("email", e.target.value)} className="text-black bg-white" data-testid="race-email" /></Field>
      <Field label="Contraseña *" hint="Mínimo 8 caracteres"><Input type="password" value={form.password} onChange={(e) => u("password", e.target.value)} className="text-black bg-white" data-testid="race-pw" /></Field>
      <Field label="Confirmar contraseña *"><Input type="password" value={form.password2} onChange={(e) => u("password2", e.target.value)} className="text-black bg-white" data-testid="race-pw2" /></Field>
      <label className="flex items-start gap-2 pt-1 cursor-pointer">
        <input type="checkbox" checked={form.privacy_accepted} onChange={(e) => u("privacy_accepted", e.target.checked)} data-testid="race-privacy" />
        <span className="text-xs text-black">Acepto aviso de privacidad y reglamento{brand ? ` ${brand}` : ""}</span>
      </label>
    </section>
  );
}

function Step2({ form, u, theme }) {
  const toggleCondition = (c) => u("conditions", form.conditions.includes(c) ? form.conditions.filter((x) => x !== c) : [...form.conditions, c]);
  return (
    <section data-testid="race-step-2" className="space-y-3 text-black">
      <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-black">Casi lista/o</h2>
      <p className="text-sm text-black/80">Es por tu seguridad en ruta. Solo la ve el staff médico.</p>
      <Field label="Edad *"><Input type="number" value={form.age} onChange={(e) => u("age", e.target.value)} className="text-black bg-white" data-testid="race-age" /></Field>
      <Field label="Tipo de sangre *">
        <Select value={form.blood_type || undefined} onValueChange={(v) => u("blood_type", v)}>
          <SelectTrigger className="bg-white border-gray-300 h-10" data-testid="race-blood"><SelectValue placeholder="Selecciona" /></SelectTrigger>
          <SelectContent>{BLOODS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="¿Padeces alguna condición médica?">
        <div className="flex flex-wrap gap-1.5">
          {CONDITIONS.map((c) => (
            <button key={c} type="button" onClick={() => toggleCondition(c)} className={`text-xs px-3 py-1 rounded-full border ${form.conditions.includes(c) ? "text-white" : "bg-white text-gray-700"}`} style={form.conditions.includes(c) ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#ddd" }} data-testid={`race-cond-${c}`}>{c}</button>
          ))}
        </div>
        {form.conditions.length > 0 && !form.conditions.includes("Ninguna") && (
          <Textarea rows={2} placeholder="Cuéntanos más…" value={form.conditions_detail} onChange={(e) => u("conditions_detail", e.target.value)} className="mt-2 bg-white" data-testid="race-cond-detail" />
        )}
      </Field>
      <Field label="¿Tomas algún medicamento?">
        <div className="flex gap-2">
          <button type="button" onClick={() => u("medications", true)} className={`text-xs px-4 py-2 rounded border ${form.medications ? "text-white" : "bg-white text-gray-700"}`} style={form.medications ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#ddd" }}>Sí</button>
          <button type="button" onClick={() => u("medications", false)} className={`text-xs px-4 py-2 rounded border ${!form.medications ? "text-white" : "bg-white text-gray-700"}`} style={!form.medications ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#ddd" }}>No</button>
        </div>
        {form.medications && <Input placeholder="¿Cuál?" value={form.medications_detail} onChange={(e) => u("medications_detail", e.target.value)} className="mt-2" data-testid="race-med-detail" />}
      </Field>
      <div className="pt-3 border-t border-gray-200">
        <p className="text-sm font-bold mb-2 text-black">Contacto de emergencia</p>
        <Field label="Nombre *"><Input value={form.emergency_name} onChange={(e) => u("emergency_name", e.target.value)} className="text-black bg-white" data-testid="race-emg-name" /></Field>
        <Field label="WhatsApp *"><Input type="tel" value={form.emergency_phone} onChange={(e) => u("emergency_phone", e.target.value)} className="text-black bg-white" data-testid="race-emg-phone" /></Field>
        <Field label="Parentesco *">
          <Select value={form.emergency_relation || undefined} onValueChange={(v) => u("emergency_relation", v)}>
            <SelectTrigger className="bg-white border-gray-300 h-10" data-testid="race-emg-rel"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>{RELATIONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
    </section>
  );
}

function Step3({ form, u, theme, onSizeGuide }) {
  return (
    <section data-testid="race-step-3" className="space-y-3 text-black">
      <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-black">Tu nivel Piste & Pace</h2>
      <p className="text-sm text-black/80">Nos ayuda a colocarte en el bloque de salida correcto.</p>
      <RadioGroup label="¿Cuántas veces por semana practicas deporte? *" options={FREQUENCIES} value={form.sport_frequency} onChange={(v) => u("sport_frequency", v)} theme={theme} testid="race-freq" />
      <RadioGroup label="¿Has corrido una carrera antes? *" options={PREVIOUS} value={form.previous_race} onChange={(v) => u("previous_race", v)} theme={theme} testid="race-prev" />
      <RadioGroup label="¿Cuál es tu objetivo? *" options={OBJECTIVES} value={form.objective} onChange={(v) => u("objective", v)} theme={theme} testid="race-obj" />
      <Field label="Talla de playera oficial *">
        <div className="flex items-center gap-2 flex-wrap">
          {SIZES.map((s) => (
            <button key={s} type="button" onClick={() => u("shirt_size", s)} className={`w-12 h-12 rounded-lg border-2 font-black ${form.shirt_size === s ? "text-white" : "bg-white text-black"}`} style={form.shirt_size === s ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }} data-testid={`race-size-${s}`}>{s}</button>
          ))}
          <button type="button" onClick={onSizeGuide} className="text-xs underline ml-2 text-black" data-testid="race-size-guide-btn">Guía de tallas</button>
        </div>
      </Field>
    </section>
  );
}

function Step4({ race, form, u, theme, onSubmit, saving, sombreroStatus }) {
  const sTh = Number(sombreroStatus?.threshold || race?.sombrero_threshold || 0);
  const sRem = Number(sombreroStatus?.remaining ?? 0);
  const sombreroAvailable = sTh > 0 && sRem > 0;
  return (
    <section data-testid="race-step-4" className="space-y-4 text-black">
      <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase text-black">Tu kit ${race.kit_price_mxn} MXN</h2>
      <p className="text-sm text-black/80">Costo único, todo incluido.</p>

      {sTh > 0 && (
        <div className={`p-3 rounded-lg border-2 ${sombreroAvailable ? "border-green-500 bg-green-50" : "border-gray-400 bg-gray-50"}`} data-testid="race-sombrero-banner">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎩</span>
            <div className="flex-1">
              {sombreroAvailable ? (
                <>
                  <p className="text-sm font-bold text-black">¡Aún hay sombrero para ti!</p>
                  <p className="text-xs text-black/70">Quedan <b>{sRem}</b> sombreros de {sTh}. Paga hoy y asegura el tuyo.</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold text-black">Los primeros {sTh} sombreros ya se asignaron</p>
                  <p className="text-xs text-black/70">Trae tu propio sombrero el día del evento para lucir la vibra {race.brand || race.name}.</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="p-4 rounded-lg" style={{ background: theme.primary_soft }}>
        <p className="text-xs uppercase tracking-widest font-bold mb-2 text-black">Incluye:</p>
        <ul className="text-sm space-y-1 text-black">
          {race.kit_items?.map((it) => <li key={it}>• {it}</li>)}
        </ul>
        <p className="text-xs uppercase tracking-widest font-bold mt-3 mb-1 text-black">Al cruzar la meta:</p>
        <p className="text-sm text-black">{race.meta_extras?.join(" · ")}</p>
      </div>
      <p className="text-xs text-black/80">Método de pago:</p>
      <div className="grid grid-cols-2 gap-2" data-testid="race-payment-methods">
        <button onClick={() => u("payment_method", "stripe")} className={`p-3 border-2 rounded-lg text-left ${form.payment_method === "stripe" ? "text-white" : "bg-white text-black"}`} style={form.payment_method === "stripe" ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }} data-testid="race-pay-stripe">
          <CreditCard size={20} weight="fill" /><p className="text-xs font-bold mt-1">Tarjeta / OXXO</p><p className="text-[10px] opacity-80">Pago inmediato</p>
        </button>
        <button onClick={() => u("payment_method", "transfer")} className={`p-3 border-2 rounded-lg text-left ${form.payment_method === "transfer" ? "text-white" : "bg-white text-black"}`} style={form.payment_method === "transfer" ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }} data-testid="race-pay-transfer">
          <CurrencyDollar size={20} weight="fill" /><p className="text-xs font-bold mt-1">Transferencia</p><p className="text-[10px] opacity-80">Verificación manual</p>
        </button>
      </div>

      {form.payment_method === "transfer" && race.transfer_info && (race.transfer_info.account_number || race.transfer_info.clabe) && (
        <div className="p-4 rounded-lg border-2 bg-white text-black" style={{ borderColor: theme.primary }} data-testid="race-transfer-info">
          <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: theme.primary }}>Datos para transferir ${race.kit_price_mxn} MXN</p>
          <ul className="text-sm space-y-1">
            {race.transfer_info.bank && <li><span className="font-bold">Banco:</span> {race.transfer_info.bank}</li>}
            {race.transfer_info.account_holder && <li><span className="font-bold">Titular:</span> {race.transfer_info.account_holder}</li>}
            {race.transfer_info.account_number && <li><span className="font-bold">Cuenta:</span> <span className="font-mono">{race.transfer_info.account_number}</span></li>}
            {race.transfer_info.clabe && <li><span className="font-bold">CLABE:</span> <span className="font-mono">{race.transfer_info.clabe}</span></li>}
          </ul>
          {race.transfer_info.reference_hint && (
            <p className="text-xs mt-2 italic" style={{ color: theme.primary }}>📌 {race.transfer_info.reference_hint}</p>
          )}
          <p className="text-xs mt-2 text-black/70">Tras transferir, sube tu comprobante desde la pantalla siguiente para que el admin confirme.</p>
        </div>
      )}

      {/* Deslinde médico */}
      <div className="p-3 rounded-lg border-2 border-yellow-500 bg-yellow-50 text-black" data-testid="race-waiver-block">
        <p className="text-xs uppercase tracking-widest font-bold mb-2 text-black">🩺 Deslinde de responsabilidad médica</p>
        <div className="text-xs leading-relaxed bg-white p-2 rounded max-h-40 overflow-y-auto whitespace-pre-wrap" data-testid="race-waiver-text">
          {race.waiver_text || "DYD y Piste & Pace no se hacen responsables por temas médicos. Únicamente contamos con apoyo de ambulancia para primeros auxilios."}
        </div>
        <label className="flex items-start gap-2 mt-2 cursor-pointer">
          <input type="checkbox" checked={form.waiver_accepted} onChange={(e) => u("waiver_accepted", e.target.checked)} data-testid="race-waiver-accept" className="mt-0.5" />
          <span className="text-xs text-black">Acepto el deslinde médico y confirmo que la información médica del paso 2 es veraz.</span>
        </label>
      </div>

      <Button onClick={() => onSubmit(form.payment_method)} disabled={saving || !form.waiver_accepted} className="w-full text-white font-bold uppercase h-12 disabled:opacity-60" style={{ background: theme.primary }} data-testid="race-submit-btn">
        {saving ? "Procesando…" : `Pagar $${race.kit_price_mxn} y asegurar mi número`}
      </Button>
    </section>
  );
}

function SuccessCard({ race, reg, onOpenManagers }) {
  const t = race.theme || {};
  const bibUrl = `${BACKEND_URL}/api/races/${race.id}/registrations/${reg.id}/bib.pdf`;
  const qrPayload = JSON.stringify({ race: race.id, bib: reg.bib_number, name: reg.full_name });
  const [regState, setRegState] = useState(reg);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const needsReceipt = regState.payment_method === "transfer" && regState.payment_status !== "paid" && !regState.receipt_path;
  const awaiting = regState.payment_status === "awaiting_review";

  const kitSummary = (race.kit_items && race.kit_items.length)
    ? race.kit_items.slice(0, 3).join(", ").toLowerCase()
    : "playera, bib y accesorios";
  const runnerLabel = race.runner_label || "Runner";
  const sombreroActive = Number(race.sombrero_threshold || 0) > 0;

  return (
    <div className="max-w-lg mx-auto text-center space-y-4" data-testid="race-success">
      <div className="p-6 rounded-lg" style={{ background: t.primary_soft }}>
        <CheckCircle size={48} weight="fill" className="mx-auto mb-2 text-black" />
        <h2 className="font-['Barlow_Condensed'] text-4xl font-black uppercase text-black">¡Ya eres {runnerLabel} #{regState.bib_number}!</h2>
        <p className="text-sm mt-2 text-black">
          {regState.payment_status === "paid" ? "Tu pago está confirmado" :
           awaiting ? "Comprobante recibido — el admin lo está revisando" :
           needsReceipt ? "Sube tu comprobante para asegurar tu registro" :
           "Tu pago está pendiente"}. Kit reservado: {kitSummary}.
        </p>
        <div className="bg-white p-3 inline-block rounded mt-4">
          <QRCodeSVG value={qrPayload} size={140} data-testid="race-qr" />
        </div>
      </div>

      {/* Sombrero badge — solo si la carrera tiene sombreros habilitados y el usuario pagó */}
      {sombreroActive && regState.payment_status === "paid" && (
        regState.sombrero_earned ? (
          <div className="p-4 rounded-lg border-4 border-yellow-500 bg-yellow-50" data-testid="race-sombrero-earned">
            <p className="text-4xl mb-1">🎩</p>
            <p className="font-['Barlow_Condensed'] text-2xl font-black uppercase text-black">¡Alcanzaste sombrero!</p>
            <p className="text-xs text-black/80 mt-1">{race?.sombrero_reached_msg || "Tu sombrero está reservado — recógelo el día del evento."}</p>
          </div>
        ) : (
          <div className="p-4 rounded-lg border-2 border-gray-400 bg-gray-50" data-testid="race-sombrero-missed">
            <p className="text-4xl mb-1">🤠</p>
            <p className="font-['Barlow_Condensed'] text-xl font-black uppercase text-black">Trae tu propio sombrero</p>
            <p className="text-xs text-black/80 mt-1">{race?.sombrero_missed_msg || `Los primeros sombreros ya fueron asignados. ¡Trae el tuyo para la vibra ${race.brand || race.name}!`}</p>
          </div>
        )
      )}

      {needsReceipt && (
        <div className="p-3 rounded-lg border-2 border-dashed bg-yellow-50" style={{ borderColor: t.primary }} data-testid="race-receipt-banner">
          <p className="text-sm font-bold text-black">⚠️ Falta tu comprobante de transferencia</p>
          {race.transfer_info && (race.transfer_info.account_number || race.transfer_info.clabe) && (
            <div className="mt-2 text-xs text-left inline-block bg-white p-2 rounded border" style={{ borderColor: t.primary + "40" }}>
              {race.transfer_info.bank && <div><b>Banco:</b> {race.transfer_info.bank}</div>}
              {race.transfer_info.account_holder && <div><b>Titular:</b> {race.transfer_info.account_holder}</div>}
              {race.transfer_info.account_number && <div><b>Cuenta:</b> <span className="font-mono">{race.transfer_info.account_number}</span></div>}
              {race.transfer_info.clabe && <div><b>CLABE:</b> <span className="font-mono">{race.transfer_info.clabe}</span></div>}
            </div>
          )}
          <p className="text-xs mt-2 text-black">Sube foto o PDF de tu comprobante para que el admin confirme tu pago.</p>
          <Button onClick={() => setReceiptOpen(true)} className="mt-2 text-white font-bold uppercase" style={{ background: t.primary }} data-testid="race-open-receipt">
            <Package size={14} className="mr-1" /> Subir comprobante
          </Button>
        </div>
      )}

      <div className="flex flex-wrap justify-center gap-2">
        <Button asChild className="text-white font-bold uppercase" style={{ background: t.primary }}>
          <a href={bibUrl} target="_blank" rel="noreferrer" data-testid="race-download-bib"><Download size={14} className="mr-1" /> Descargar bib PDF</a>
        </Button>
      </div>
      <p className="text-xs text-gray-500">Te enviamos todo a tu WhatsApp y correo.</p>

      <ReceiptUploadDialog
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        raceId={race.id}
        regId={regState.id}
        theme={t}
        onUploaded={(uploaded_at) => { setRegState((r) => ({ ...r, receipt_path: "uploaded", receipt_uploaded_at: uploaded_at, payment_status: "awaiting_review" })); setReceiptOpen(false); }}
      />
    </div>
  );
}

function ReceiptUploadDialog({ open, onClose, raceId, regId, theme, onUploaded }) {
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!file) { toast.error("Elige un archivo"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/races/${raceId}/registrations/${regId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante subido");
      onUploaded?.(data.uploaded_at);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="race-receipt-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>Sube tu comprobante</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-gray-600">Foto o PDF del comprobante de transferencia (máx 10MB).</p>
          <Input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0])} data-testid="race-receipt-file" />
          {file && <p className="text-[11px] text-gray-500">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</p>}
        </div>
        <div className="flex gap-2 justify-end mt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!file || saving} className="text-white font-bold" style={{ background: theme.primary }} data-testid="race-receipt-submit">{saving ? "Subiendo…" : "Enviar comprobante"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs font-bold text-gray-700">{label}</Label>
      {hint && <p className="text-[10px] text-gray-500 mb-1">[{hint}]</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function RadioGroup({ label, options, value, onChange, theme, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)} className={`text-xs px-3 py-1.5 rounded-full border ${value === o ? "text-white" : "bg-white text-gray-700"}`} style={value === o ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#ddd" }} data-testid={`${testid}-${o.slice(0,8)}`}>{o}</button>
        ))}
      </div>
    </Field>
  );
}

function SizeGuideDialog({ open, onClose, theme, race }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="race-size-guide-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>Guía de tallas {race?.brand || race?.name || ""}</DialogTitle></DialogHeader>
        <table className="w-full text-sm">
          <thead style={{ background: theme.primary_soft, color: theme.primary }}>
            <tr><th className="p-2 text-left">Talla</th><th className="p-2 text-left">Busto (cm)</th><th className="p-2 text-left">Largo (cm)</th></tr>
          </thead>
          <tbody>
            {SIZE_GUIDE.map((s) => <tr key={s.size} className="border-b border-gray-100"><td className="p-2 font-bold" style={{ color: theme.primary }}>{s.size}</td><td className="p-2">{s.busto}</td><td className="p-2">{s.largo}</td></tr>)}
          </tbody>
        </table>
        <p className="text-[10px] text-gray-500">Medidas aproximadas. Si estás entre dos tallas, elige la mayor.</p>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ADMIN DASHBOARD (route: /dashboard/dyd-run/carreras/:raceId/admin)
// ============================================================
export function RaceAdminDashboard() {
  const { raceId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [regs, setRegs] = useState([]);
  const [q, setQ] = useState("");
  const [payFilter, setPayFilter] = useState("all");
  const [editReg, setEditReg] = useState(null);
  const [managersOpen, setManagersOpen] = useState(false);
  const [drillFilter, setDrillFilter] = useState(null); // 'total'|'paid'|'pending'|'delivered'
  const [scanOpen, setScanOpen] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [editRaceOpen, setEditRaceOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [myAccess, setMyAccess] = useState({ is_manager: false, permission_level: null, is_admin: false });

  const load = () => {
    api.get(`/races/${raceId}/dashboard`).then(({ data }) => setData(data)).catch((e) => toast.error(e.response?.data?.detail || "Sin acceso"));
    const p = payFilter !== "all" ? `&payment=${payFilter}` : "";
    api.get(`/races/${raceId}/registrations?q=${encodeURIComponent(q)}${p}&limit=300`).then(({ data }) => setRegs(data));
  };
  useEffect(() => { load(); }, [raceId, q, payFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/races/${raceId}/my-access`).then(({ data }) => setMyAccess(data)).catch(() => {}); }, [raceId]);

  if (!data) return <div className="p-8 text-center text-gray-400">Cargando dashboard…</div>;
  const { race, kpis, growth } = data;
  const t = race.theme || {};
  const at = race.admin_theme || { table_bg: "#FFFFFF", table_text: "#0A0A0A", table_head_bg: "#F4D5DA", table_head_text: "#5B1A2A", managers_bg: "#FFFFFF", managers_text: "#0A0A0A" };
  const isFullManager = myAccess.permission_level === "full" || user?.role === "admin";
  const isAdmin = user?.role === "admin";

  const openDrill = (kind) => {
    setDrillFilter(kind);
    if (kind === "paid") setPayFilter("paid");
    else if (kind === "pending") setPayFilter("pending_transfer");
    else setPayFilter("all");
  };

  return (
    <div className="space-y-4 pb-8" data-testid="race-admin-dashboard">
      <header className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: t.primary }}>{race.brand} · {race.name}</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl font-black uppercase tracking-tighter" style={{ color: t.primary }}>Dashboard</h1>
          <p className="text-xs text-gray-500">Registro de corredoras · {race.event_date}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setScanOpen(true)} className="text-white text-xs uppercase" style={{ background: t.primary }} data-testid="race-scan-btn">
            <QrCode size={14} className="mr-1" /> Escanear QR
          </Button>
          <Button onClick={() => setQrOpen(true)} variant="outline" className="text-xs uppercase" data-testid="race-public-qr-btn">
            <QrCode size={14} className="mr-1" /> QR Público
          </Button>
          {isFullManager && (
            <>
              <Button onClick={() => setManualOpen(true)} className="text-xs uppercase text-white" style={{ background: t.primary }} data-testid="race-manual-register-btn">
                <UserPlus size={14} className="mr-1" /> Inscribir manualmente
              </Button>
              <a
                href={`${BACKEND_URL}/api/races/${raceId}/credentials.pdf?scope=manual`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center h-9 px-3 rounded-md text-xs uppercase font-medium border bg-white hover:bg-gray-50"
                style={{ borderColor: t.primary, color: t.primary }}
                data-testid="race-credentials-pdf-manual"
              >
                <FilePdf size={14} className="mr-1" /> PDF credenciales (manuales)
              </a>
              <a
                href={`${BACKEND_URL}/api/races/${raceId}/credentials.pdf?scope=all`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center h-9 px-3 rounded-md text-xs uppercase font-medium border bg-white hover:bg-gray-50"
                style={{ borderColor: t.primary, color: t.primary }}
                data-testid="race-credentials-pdf-all"
              >
                <FilePdf size={14} className="mr-1" /> PDF (todos)
              </a>
              <Button onClick={() => setEditRaceOpen(true)} variant="outline" className="text-xs uppercase" data-testid="race-edit-btn">
                <PencilSimple size={14} className="mr-1" /> Editar carrera
              </Button>
              <Button onClick={() => setColorsOpen(true)} variant="outline" className="text-xs uppercase" data-testid="race-colors-btn">
                <Sparkle size={14} className="mr-1" /> Colores
              </Button>
            </>
          )}
          {isAdmin && (
            <Button onClick={() => setManagersOpen(true)} className="text-white text-xs uppercase" style={{ background: t.primary }} data-testid="race-managers-btn">
              <ShieldStar size={14} className="mr-1" /> Autorizados
            </Button>
          )}
        </div>
      </header>

      {/* KPI cards — clickables */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <RaceKpi icon={Users} label="Total Registradas" value={kpis.total} sub={`↑ +${kpis.new_this_week} esta semana`} theme={t} filled onClick={() => openDrill("total")} />
        <RaceKpi icon={CheckCircle} label="Pagos Confirmados" value={kpis.paid} sub={`${kpis.paid_pct}% del total · Confirmados`} theme={t} onClick={() => openDrill("paid")} />
        <RaceKpi icon={CurrencyDollar} label="Por Cobrar" value={kpis.pending} sub={`${kpis.pending_pct}% pendiente · $${kpis.pending_amount_mxn.toLocaleString()} MXN`} theme={t} onClick={() => openDrill("pending")} />
        <RaceKpi icon={Package} label="Kits Entregados" value={kpis.delivered} sub={`${kpis.delivered_pct}% entregados · ${kpis.delivered} de ${kpis.total}`} theme={t} onClick={() => openDrill("delivered")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Growth */}
        <div className="rounded-lg p-4" style={{ background: t.primary_soft }} data-testid="race-growth-chart">
          <h3 className="font-black text-xl mb-1" style={{ color: t.primary }}>Crecimiento de Registros</h3>
          <p className="text-xs mb-3" style={{ color: t.primary, opacity: 0.7 }}>Últimos 7 días · +{kpis.new_this_week} nuevas</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={growth} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" />
                <XAxis dataKey="date" stroke={t.primary} fontSize={10} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis stroke={t.primary} fontSize={10} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke={t.primary} strokeWidth={3} dot={{ r: 4 }} name="Nuevas" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Últimas inscripciones — con admin theme (negro legible por defecto) */}
        <div className="rounded-lg p-4 border" style={{ background: at.table_bg, borderColor: t.primary + "40", color: at.table_text }} data-testid="race-registrations-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-xl" style={{ color: t.primary }}>Últimas Inscripciones</h3>
          </div>
          <div className="flex gap-2 mb-2">
            <div className="relative flex-1">
              <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar corredora…" className="pl-7 h-8 text-xs" data-testid="race-search" />
            </div>
            <Select value={payFilter} onValueChange={setPayFilter}>
              <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="paid">Pagados</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="pending_transfer">Transferencia</SelectItem>
                <SelectItem value="awaiting_review">Comprobante en revisión</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs" style={{ color: at.table_text }}>
              <thead className="text-left uppercase text-[10px]" style={{ color: at.table_head_text, background: at.table_head_bg }}>
                <tr>
                  <th className="py-2 px-2">No.</th><th className="px-2">Nombre</th><th className="px-2">Talla</th><th className="px-2">Pago</th><th className="px-2">WA</th><th></th>
                </tr>
              </thead>
              <tbody>
                {regs.map((r_) => <RegistrationRow key={r_.id} reg={r_} theme={t} adminTheme={at} onEdit={() => setEditReg(r_)} raceId={raceId} onChanged={load} />)}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-gray-500 mt-2">Mostrando {regs.length} de {kpis.total} resultados</p>
        </div>
      </div>

      <EditRegDialog raceId={raceId} reg={editReg} onClose={() => setEditReg(null)} onSaved={() => { setEditReg(null); load(); }} theme={t} />
      {isAdmin && <ManagersDialog raceId={raceId} open={managersOpen} onClose={() => setManagersOpen(false)} theme={t} adminTheme={at} />}
      <DrillDialog raceId={raceId} kind={drillFilter} onClose={() => setDrillFilter(null)} theme={t} adminTheme={at} onChanged={load} />
      <ScanDialog raceId={raceId} open={scanOpen} onClose={() => setScanOpen(false)} theme={t} onChanged={load} />
      <ColorsDialog raceId={raceId} open={colorsOpen} onClose={() => setColorsOpen(false)} currentTheme={at} onSaved={() => { setColorsOpen(false); load(); }} />
      <PublicQrDialog raceId={raceId} open={qrOpen} onClose={() => setQrOpen(false)} race={race} theme={t} />
      {isFullManager && (
        <ManualRegistrationDialog raceId={raceId} race={race} open={manualOpen} onClose={() => setManualOpen(false)} onDone={load} theme={t} />
      )}
      {isFullManager && (
        <RaceEditorDialog mode="edit" race={race} open={editRaceOpen} onClose={() => setEditRaceOpen(false)} onSaved={() => { setEditRaceOpen(false); load(); }} isAdmin={isAdmin} />
      )}
    </div>
  );
}

function RaceKpi({ icon: Icon, label, value, sub, theme, filled, onClick }) {
  const bg = filled ? theme.primary_soft : "white";
  const color = theme.primary;
  return (
    <button type="button" onClick={onClick} className="rounded-lg p-4 border text-left hover:brightness-95 transition-all cursor-pointer" style={{ background: bg, borderColor: color + "40" }} data-testid={`race-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <Icon size={20} className="mb-1" weight="fill" style={{ color }} />
      <p className="text-xs font-bold" style={{ color }}>{label}</p>
      <p className="text-5xl font-black font-['Barlow_Condensed'] leading-none mt-1" style={{ color }}>{value}</p>
      <p className="text-[10px] mt-1" style={{ color, opacity: 0.7 }}>{sub}</p>
      <p className="text-[9px] mt-1 uppercase tracking-widest" style={{ color, opacity: 0.5 }}>Ver detalles ↗</p>
    </button>
  );
}

function RegistrationRow({ reg, theme, adminTheme, onEdit, raceId, onChanged }) {
  const at = adminTheme || {};
  const statusColors = {
    paid: "bg-green-500 text-white",
    pending: "bg-yellow-500 text-white",
    pending_transfer: "bg-orange-500 text-white",
    awaiting_review: "bg-blue-500 text-white",
  };
  const statusLabels = { paid: "Pagado", pending: "Pendiente", pending_transfer: "Transferencia", awaiting_review: "Revisar" };
  const wa = (reg.whatsapp || "").replace(/\D/g, "");
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50" data-testid={`race-reg-${reg.id}`}>
      <td className="py-2 px-2 font-mono font-bold" style={{ color: at.table_text || "#0A0A0A" }}>{reg.bib_number || "—"}</td>
      <td className="px-2 font-bold" style={{ color: at.table_text || "#0A0A0A" }}>{reg.full_name}</td>
      <td className="px-2 font-bold" style={{ color: at.table_text || "#0A0A0A" }}>{reg.shirt_size || "—"}</td>
      <td className="px-2"><span className={`text-[9px] px-2 py-0.5 rounded uppercase ${statusColors[reg.payment_status] || "bg-gray-500 text-white"}`}>{statusLabels[reg.payment_status] || reg.payment_status}</span></td>
      <td className="px-2">
        {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="text-green-600 text-[10px] hover:underline" data-testid={`race-wa-${reg.id}`}><WhatsappLogo size={12} weight="fill" className="inline" /> {reg.whatsapp}</a>}
      </td>
      <td className="px-2"><button onClick={onEdit} className="text-xs" style={{ color: theme.primary }} data-testid={`race-edit-${reg.id}`}><PencilSimple size={12} /></button></td>
    </tr>
  );
}

// ============================================================
// KPI DRILL-DOWN DIALOG
// ============================================================
function DrillDialog({ raceId, kind, onClose, theme, adminTheme, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!kind) return;
    setLoading(true);
    let q = "";
    if (kind === "paid") q = "&payment=paid";
    else if (kind === "pending") q = "&payment=pending_transfer";
    api.get(`/races/${raceId}/registrations?limit=500${q}`)
      .then(({ data }) => {
        if (kind === "delivered") setRows(data.filter((d) => d.kit_delivered));
        else if (kind === "pending") setRows(data.filter((d) => d.payment_status !== "paid"));
        else setRows(data);
      }).finally(() => setLoading(false));
  }, [kind, raceId]);
  if (!kind) return null;
  const titles = { total: "Todas las registradas", paid: "Pagos confirmados", pending: "Por cobrar", delivered: "Kits entregados" };
  const confirm = async (id) => { await api.post(`/races/${raceId}/registrations/${id}/confirm-payment`); toast.success("Pago confirmado"); onChanged?.(); setRows((rs) => rs.map((r) => r.id === id ? { ...r, payment_status: "paid" } : r)); };
  const deliver = async (id) => { await api.post(`/races/${raceId}/registrations/${id}/deliver-kit`); toast.success("Kit entregado"); onChanged?.(); setRows((rs) => rs.map((r) => r.id === id ? { ...r, kit_delivered: true } : r)); };
  return (
    <Dialog open={!!kind} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="race-drill-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>{titles[kind]} — {rows.length}</DialogTitle></DialogHeader>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="p-8 text-center">Cargando…</div> : (
            <table className="w-full text-xs">
              <thead className="text-left uppercase text-[10px] sticky top-0" style={{ background: adminTheme?.table_head_bg, color: adminTheme?.table_head_text }}>
                <tr>
                  <th className="p-2">No.</th><th>Nombre</th><th>Talla</th><th>WA</th><th>Comprobante</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody style={{ color: adminTheme?.table_text || "#0A0A0A" }}>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100" data-testid={`drill-row-${r.id}`}>
                    <td className="p-2 font-mono font-bold">{r.bib_number}</td>
                    <td className="font-bold">{r.full_name}</td>
                    <td className="font-bold">{r.shirt_size || "—"}</td>
                    <td>{r.whatsapp}</td>
                    <td>
                      {r.receipt_path ? <a href={`${BACKEND_URL}/api/races/${raceId}/registrations/${r.id}/receipt/media`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-[10px]" data-testid={`drill-receipt-${r.id}`}>Ver</a> : <span className="text-gray-400 text-[10px]">—</span>}
                    </td>
                    <td className="p-1 flex gap-1 flex-wrap">
                      {r.payment_status !== "paid" && <Button size="sm" onClick={() => confirm(r.id)} className="h-6 text-[10px] bg-green-600 hover:bg-green-700 text-white px-2" data-testid={`drill-confirm-${r.id}`}><CheckCircle size={10} className="mr-1" /> Confirmar pago</Button>}
                      {!r.kit_delivered && <Button size="sm" onClick={() => deliver(r.id)} className="h-6 text-[10px] px-2 text-white" style={{ background: theme.primary }} data-testid={`drill-deliver-${r.id}`}><Package size={10} className="mr-1" /> Entregar kit</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// SCAN QR DIALOG
// ============================================================
function ScanDialog({ raceId, open, onClose, theme, onChanged }) {
  const [scanned, setScanned] = useState(null);
  const [error, setError] = useState("");
  const scannerRef = useState({ current: null })[0];

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode("race-qr-region");
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          async (decoded) => {
            if (cancelled) return;
            cancelled = true;
            try { await scanner.stop(); scanner.clear(); } catch { /* noop */ }
            try {
              const { data } = await api.get(`/races/${raceId}/scan/${encodeURIComponent(decoded)}`);
              setScanned(data);
            } catch (e) { setError(e.response?.data?.detail || "Corredora no encontrada"); }
          }
        );
      } catch (e) { setError(String(e)); }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) { try { s.stop().then(() => s.clear()).catch(() => {}); } catch { /* noop */ } }
    };
  }, [open, raceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => { await api.post(`/races/${raceId}/registrations/${scanned.id}/confirm-payment`); toast.success("Pago confirmado"); onChanged?.(); setScanned({ ...scanned, payment_status: "paid" }); };
  const deliver = async () => { await api.post(`/races/${raceId}/registrations/${scanned.id}/deliver-kit`); toast.success("Kit entregado"); onChanged?.(); setScanned({ ...scanned, kit_delivered: true }); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setScanned(null); setError(""); onClose(); } }}>
      <DialogContent className="bg-white max-w-md" data-testid="race-scan-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>Escanear QR de corredora</DialogTitle></DialogHeader>
        {!scanned ? (
          <>
            <div id="race-qr-region" className="w-full min-h-[280px] bg-black rounded" />
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
          </>
        ) : (
          <div className="space-y-3">
            <div className="p-4 rounded-lg text-center" style={{ background: theme.primary_soft, color: theme.primary }}>
              <p className="text-xs uppercase tracking-widest">Bib #{scanned.bib_number}</p>
              <p className="text-2xl font-black font-['Barlow_Condensed']">{scanned.full_name}</p>
              <p className="text-xs mt-1">Talla {scanned.shirt_size} · {scanned.whatsapp}</p>
              <div className="flex justify-center gap-2 mt-2 text-[11px] font-bold">
                <span className={scanned.payment_status === "paid" ? "text-green-600" : "text-orange-600"}>Pago: {scanned.payment_status}</span>
                <span className={scanned.kit_delivered ? "text-green-600" : "text-gray-600"}>Kit: {scanned.kit_delivered ? "entregado" : "pendiente"}</span>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {scanned.payment_status !== "paid" && <Button onClick={confirm} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold text-xs uppercase" data-testid="scan-confirm-payment"><CheckCircle size={14} className="mr-1" /> Confirmar pago</Button>}
              {!scanned.kit_delivered && <Button onClick={deliver} className="flex-1 text-white font-bold text-xs uppercase" style={{ background: theme.primary }} data-testid="scan-deliver-kit"><Package size={14} className="mr-1" /> Entregar kit</Button>}
              <Button variant="outline" onClick={() => { setScanned(null); onClose(); setTimeout(onClose, 100); }} className="text-xs">Cerrar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// COLORS DIALOG (admin personaliza colores)
// ============================================================
function ColorsDialog({ raceId, open, onClose, currentTheme, onSaved }) {
  const [colors, setColors] = useState(currentTheme || {});
  useEffect(() => { setColors(currentTheme || {}); }, [currentTheme, open]);
  const set = (k, v) => setColors((c) => ({ ...c, [k]: v }));
  const save = async () => {
    try { await api.patch(`/races/${raceId}/admin-theme`, colors); toast.success("Colores actualizados"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const reset = () => setColors({ table_bg: "#FFFFFF", table_text: "#0A0A0A", table_head_bg: "#F4D5DA", table_head_text: "#5B1A2A", managers_bg: "#FFFFFF", managers_text: "#0A0A0A" });
  const rows = [
    { k: "table_bg", label: "Fondo tabla Inscripciones" },
    { k: "table_text", label: "Texto tabla (números, nombres, tallas)" },
    { k: "table_head_bg", label: "Fondo encabezado tabla" },
    { k: "table_head_text", label: "Texto encabezado" },
    { k: "managers_bg", label: "Fondo Autorizados" },
    { k: "managers_text", label: "Texto Autorizados" },
  ];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="race-colors-dialog">
        <DialogHeader><DialogTitle>Personalizar colores del dashboard</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.k} className="flex items-center justify-between gap-3 py-1 border-b border-gray-100">
              <Label className="text-xs flex-1">{r.label}</Label>
              <input type="color" value={colors[r.k] || "#000000"} onChange={(e) => set(r.k, e.target.value)} className="h-9 w-14 rounded cursor-pointer" data-testid={`color-${r.k}`} />
              <span className="text-[10px] font-mono w-16 text-right">{colors[r.k]}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between gap-2 mt-2">
          <Button variant="outline" onClick={reset} className="text-xs">Restablecer defaults</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={save} className="bg-black text-white font-bold" data-testid="race-colors-save">Guardar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditRegDialog({ raceId, reg, onClose, onSaved, theme }) {
  const [form, setForm] = useState({});
  useEffect(() => { if (reg) setForm({ bib_number: reg.bib_number || "", payment_status: reg.payment_status, kit_delivered: reg.kit_delivered, shirt_size: reg.shirt_size || "", notes: reg.notes || "" }); }, [reg]);
  if (!reg) return null;
  const save = async () => {
    try {
      await api.patch(`/races/${raceId}/registrations/${reg.id}`, form);
      toast.success("Guardado");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <Dialog open={!!reg} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white max-w-md" data-testid="race-edit-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>Editar corredora — {reg.full_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Bib"><Input value={form.bib_number || ""} onChange={(e) => setForm({ ...form, bib_number: e.target.value })} data-testid="race-edit-bib" /></Field>
          <Field label="Estatus de pago">
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger data-testid="race-edit-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paid">Pagado</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="pending_transfer">Transferencia</SelectItem>
                <SelectItem value="refunded">Reembolsado</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Talla">
            <Select value={form.shirt_size || undefined} onValueChange={(v) => setForm({ ...form, shirt_size: v })}>
              <SelectTrigger data-testid="race-edit-size"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.kit_delivered} onChange={(e) => setForm({ ...form, kit_delivered: e.target.checked })} data-testid="race-edit-kit" />
            <span className="text-sm">Kit entregado</span>
          </label>
          <Textarea rows={2} placeholder="Notas internas" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="race-edit-notes" />
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} className="text-white font-bold" style={{ background: theme.primary }} data-testid="race-edit-save">Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManagersDialog({ raceId, open, onClose, theme, adminTheme }) {
  const [managers, setManagers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("full");
  const at = adminTheme || {};
  const load = () => api.get(`/races/${raceId}/managers`).then(({ data }) => setManagers(data));
  useEffect(() => { if (open) load(); }, [open, raceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const tm = setTimeout(() => api.get(`/races/${raceId}/managers/candidates?q=${encodeURIComponent(q)}`).then(({ data }) => setCandidates(data)), 250);
    return () => clearTimeout(tm);
  }, [q, open, raceId]);
  const activeIds = new Set(managers.map((m) => m.user_id));
  const filtered = candidates.filter((c) => !activeIds.has(c.id));
  const grant = async (uid) => { await api.post(`/races/${raceId}/managers`, { user_id: uid, permission_level: level }); toast.success(`Autorizado (${level === "full" ? "acceso total" : "solo registros"})`); load(); };
  const revoke = async (uid) => { await api.delete(`/races/${raceId}/managers/${uid}`); toast.success("Removido"); load(); };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" style={{ background: at.managers_bg || "#FFFFFF", color: at.managers_text || "#0A0A0A" }} data-testid="race-managers-dialog">
        <DialogHeader><DialogTitle style={{ color: theme.primary }}>Directivos autorizados</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Autorizados ({managers.length})</p>
            {managers.length === 0 ? <p className="text-sm italic text-gray-500">Sin autorizados. Solo admin ve el dashboard.</p> : (
              <ul className="space-y-1.5">{managers.map((m) => (
                <li key={m.user_id} className="flex items-center gap-2 p-2 border rounded" data-testid={`race-manager-${m.user_id}`}>
                  <UserAvatar src={m.user?.profile_photo_url} fallback={(m.user?.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                  <span className="flex-1 text-sm font-bold" style={{ color: at.managers_text || "#0A0A0A" }}>{m.user?.name || m.user_id}</span>
                  <Badge className="text-[9px] uppercase" style={{ background: m.permission_level === "full" ? theme.primary : "#8a8a8a", color: "white" }}>{m.permission_level === "full" ? "Total" : "Registros"}</Badge>
                  <Button size="sm" variant="outline" onClick={() => revoke(m.user_id)} className="text-red-500 h-7 text-[10px]">Revocar</Button>
                </li>
              ))}</ul>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Nivel a otorgar</p>
            <div className="flex gap-2 mb-2" data-testid="race-managers-level-picker">
              <button type="button" onClick={() => setLevel("full")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "full" ? "text-white" : "bg-white text-black"}`} style={level === "full" ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }}>Acceso Total (como admin)</button>
              <button type="button" onClick={() => setLevel("registration")} className={`flex-1 py-2 rounded border-2 text-xs font-bold ${level === "registration" ? "text-white" : "bg-white text-black"}`} style={level === "registration" ? { background: theme.primary, borderColor: theme.primary } : { borderColor: "#c9c9c9" }}>Solo Registros</button>
            </div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Añadir</p>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="mb-2" data-testid="race-managers-search" />
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {filtered.map((c) => (
                <li key={c.id} className="flex items-center gap-2 p-2 border rounded">
                  <UserAvatar src={c.profile_photo_url} fallback={(c.name || "?").slice(0,2)} className="w-7 h-7 rounded-full" />
                  <span className="flex-1 text-sm">{c.name} <span className="text-[10px] text-gray-500 uppercase">({c.role})</span></span>
                  <Button size="sm" onClick={() => grant(c.id)} className="text-white h-7 text-[10px]" style={{ background: theme.primary }} data-testid={`race-manager-add-${c.id}`}><UserPlus size={12} className="mr-1" /> Autorizar</Button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function PublicQrDialog({ raceId, open, onClose, race, theme }) {
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const targetUrl = `${window.location.origin}/dashboard/dyd-run/carreras/${raceId}`;
  useEffect(() => {
    if (!open) { setQrBlobUrl(null); return; }
    api.get(`/races/${raceId}/public-qr.png`, { responseType: "blob" })
      .then(({ data }) => setQrBlobUrl(URL.createObjectURL(data)))
      .catch(() => toast.error("No se pudo generar el QR"));
    return () => { if (qrBlobUrl) URL.revokeObjectURL(qrBlobUrl); };
  }, [open, raceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = () => {
    if (!qrBlobUrl) return;
    const a = document.createElement("a");
    a.href = qrBlobUrl;
    a.download = `qr_${raceId}.png`;
    a.click();
  };
  const copyUrl = () => { navigator.clipboard.writeText(targetUrl); toast.success("URL copiada"); };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white text-black" data-testid="race-public-qr-dialog">
        <DialogHeader><DialogTitle style={{ color: theme?.primary }}>QR público para inscripción</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-black/80">Imprime este QR en flyers y redes. Al escanearlo, el corredor va directo a la pantalla de inscripción y pago.</p>
          <div className="flex justify-center bg-white p-4 rounded border">
            {qrBlobUrl ? <img src={qrBlobUrl} alt="QR VaqueRun" className="w-64 h-64" data-testid="race-public-qr-img" /> :
              <div className="w-64 h-64 flex items-center justify-center text-xs text-gray-400">Generando…</div>}
          </div>
          <div className="text-[10px] text-black/60 text-center break-all bg-gray-50 p-2 rounded">{targetUrl}</div>
          <div className="flex gap-2 justify-center">
            <Button onClick={copyUrl} variant="outline" data-testid="race-qr-copy"><ChartLine size={14} className="mr-1" /> Copiar URL</Button>
            <Button onClick={download} className="text-white font-bold" style={{ background: theme?.primary }} disabled={!qrBlobUrl} data-testid="race-qr-download"><Download size={14} className="mr-1" /> Descargar PNG</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// RACE EDITOR DIALOG (create + edit) — Admin
// ============================================================

const GENDER_MODES = [
  { value: "mixed", label: "Mixto (Hombres y Mujeres)" },
  { value: "women", label: "Solo Mujeres" },
  { value: "men", label: "Solo Hombres" },
];

function RaceEditorDialog({ mode = "edit", race, open, onClose, onSaved, isAdmin = true, defaultBrandId = null }) {
  const isCreate = mode === "create";
  const empty = {
    name: "", brand: "", brand_id: defaultBrandId || "", subtitle: "",
    runner_label: "Runner", welcome_enabled: false, welcome_title: "", welcome_message: "",
    event_date: "", location: "", currency: "MXN",
    kit_price_mxn: 400, distance_km: 5, gender_mode: "mixed",
    kit_items: ["", "", "", ""], meta_extras: [""],
    shirt_sizes: ["XS", "S", "M", "L", "XL", "XXL"],
    genders_enabled: true, active: true,
    sombrero_threshold: 0,
    sombrero_reached_msg: "",
    sombrero_missed_msg: "",
    waiver_text: "Los organizadores no se hacen responsables por temas médicos. Únicamente contamos con apoyo de ambulancia para primeros auxilios.",
    theme: { primary: "#5B1A2A", primary_soft: "#F4D5DA", accent: "#B85A6F" },
    transfer_info: { bank: "", account_holder: "", account_number: "", clabe: "", reference_hint: "Poner tu nombre completo como referencia" },
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/race-brands").then(({ data }) => setBrands(data?.brands || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isCreate) setForm(empty);
    else if (race) {
      setForm({
        ...empty, ...race,
        kit_items: race.kit_items?.length ? race.kit_items : empty.kit_items,
        meta_extras: race.meta_extras?.length ? race.meta_extras : empty.meta_extras,
        shirt_sizes: race.shirt_sizes?.length ? race.shirt_sizes : empty.shirt_sizes,
        theme: { ...empty.theme, ...(race.theme || {}) },
        transfer_info: { ...empty.transfer_info, ...(race.transfer_info || {}) },
      });
    }
  }, [open, race, isCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const uTi = (k, v) => setForm((f) => ({ ...f, transfer_info: { ...f.transfer_info, [k]: v } }));
  const uTh = (k, v) => setForm((f) => ({ ...f, theme: { ...f.theme, [k]: v } }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      // Sanitize arrays
      const payload = {
        ...form,
        kit_items: (form.kit_items || []).map((s) => (s || "").trim()).filter(Boolean),
        meta_extras: (form.meta_extras || []).map((s) => (s || "").trim()).filter(Boolean),
        shirt_sizes: (form.shirt_sizes || []).map((s) => (s || "").trim()).filter(Boolean),
        kit_price_mxn: Number(form.kit_price_mxn) || 0,
        distance_km: Number(form.distance_km) || 0,
      };
      if (isCreate) {
        await api.post("/races", payload);
        toast.success("Carrera creada");
      } else {
        await api.patch(`/races/${race.id}`, payload);
        toast.success("Carrera actualizada");
      }
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const deleteRace = async () => {
    if (!window.confirm(`¿Eliminar la carrera "${race.name}"? Si tiene inscripciones, solo se desactivará.`)) return;
    try {
      const { data } = await api.delete(`/races/${race.id}`);
      toast.success(data.message || "Eliminada");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setKitItem = (idx, val) => {
    const arr = [...form.kit_items]; arr[idx] = val; setForm((f) => ({ ...f, kit_items: arr }));
  };
  const addKitItem = () => setForm((f) => ({ ...f, kit_items: [...f.kit_items, ""] }));
  const removeKitItem = (idx) => setForm((f) => ({ ...f, kit_items: f.kit_items.filter((_, i) => i !== idx) }));

  const setExtra = (idx, val) => {
    const arr = [...form.meta_extras]; arr[idx] = val; setForm((f) => ({ ...f, meta_extras: arr }));
  };
  const addExtra = () => setForm((f) => ({ ...f, meta_extras: [...f.meta_extras, ""] }));
  const removeExtra = (idx) => setForm((f) => ({ ...f, meta_extras: f.meta_extras.filter((_, i) => i !== idx) }));

  const toggleSize = (s) => setForm((f) => ({
    ...f,
    shirt_sizes: f.shirt_sizes.includes(s) ? f.shirt_sizes.filter((x) => x !== s) : [...f.shirt_sizes, s],
  }));
  const ALL_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-white text-black" data-testid="race-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-black font-['Barlow_Condensed'] text-2xl uppercase">
            {isCreate ? "Crear nueva carrera" : `Editar ${race?.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Básicos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Nombre *"><Input value={form.name} onChange={(e) => u("name", e.target.value)} className="text-black bg-white" data-testid="race-editor-name" /></Field>
            <Field label="Marca (texto libre)"><Input value={form.brand} onChange={(e) => u("brand", e.target.value)} className="text-black bg-white" data-testid="race-editor-brand" /></Field>
            <Field label="Marca enlazada" className="sm:col-span-2">
              <Select value={form.brand_id || "__none__"} onValueChange={(v) => u("brand_id", v === "__none__" ? "" : v)}>
                <SelectTrigger className="bg-white text-black" data-testid="race-editor-brand-id"><SelectValue placeholder="Sin marca" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin marca</SelectItem>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Nombre del corredor" hint="Ej: VaqueRunner, CorreWeener, Runner"><Input value={form.runner_label} onChange={(e) => u("runner_label", e.target.value)} className="text-black bg-white" data-testid="race-editor-runner-label" /></Field>
            <Field label="Subtítulo" className="sm:col-span-2"><Input value={form.subtitle} onChange={(e) => u("subtitle", e.target.value)} className="text-black bg-white" data-testid="race-editor-subtitle" /></Field>
            <Field label="Fecha (YYYY-MM-DD)"><Input value={form.event_date} onChange={(e) => u("event_date", e.target.value)} placeholder="2027-09-14" className="text-black bg-white" data-testid="race-editor-date" /></Field>
            <Field label="Ubicación"><Input value={form.location} onChange={(e) => u("location", e.target.value)} className="text-black bg-white" data-testid="race-editor-location" /></Field>
            <Field label="Precio kit (MXN)"><Input type="number" value={form.kit_price_mxn} onChange={(e) => u("kit_price_mxn", e.target.value)} className="text-black bg-white" data-testid="race-editor-price" /></Field>
            <Field label="Distancia (km)"><Input type="number" step="0.1" value={form.distance_km} onChange={(e) => u("distance_km", e.target.value)} className="text-black bg-white" data-testid="race-editor-distance" /></Field>
            <Field label="Modalidad" className="sm:col-span-2">
              <Select value={form.gender_mode} onValueChange={(v) => u("gender_mode", v)}>
                <SelectTrigger className="bg-white text-black" data-testid="race-editor-gender-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{GENDER_MODES.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          {/* Bienvenida (mensaje hero arriba del formulario público) */}
          <div className="p-3 rounded-lg border-2 border-black/10 bg-amber-50">
            <label className="flex items-center gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={!!form.welcome_enabled} onChange={(e) => u("welcome_enabled", e.target.checked)} data-testid="race-editor-welcome-enabled" />
              <span className="text-xs uppercase tracking-widest text-black font-bold">Mostrar mensaje de bienvenida</span>
            </label>
            {form.welcome_enabled && (
              <div className="space-y-2">
                <Input value={form.welcome_title} onChange={(e) => u("welcome_title", e.target.value)} placeholder="Título (Ej: Únete a VaqueRun)" className="text-black bg-white" data-testid="race-editor-welcome-title" />
                <Textarea rows={3} value={form.welcome_message} onChange={(e) => u("welcome_message", e.target.value)} placeholder="Mensaje de bienvenida que aparece en la pantalla pública de inscripción." className="text-black bg-white" data-testid="race-editor-welcome-message" />
              </div>
            )}
          </div>

          {/* Tallas */}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Tallas disponibles</p>
            <div className="flex flex-wrap gap-1.5" data-testid="race-editor-sizes">
              {ALL_SIZES.map((s) => (
                <button key={s} type="button" onClick={() => toggleSize(s)}
                  className={`w-11 h-10 rounded border-2 text-xs font-bold ${form.shirt_sizes.includes(s) ? "text-white" : "bg-white text-black"}`}
                  style={form.shirt_sizes.includes(s) ? { background: form.theme.primary, borderColor: form.theme.primary } : { borderColor: "#c9c9c9" }}
                  data-testid={`race-editor-size-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          {/* Kit items */}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Elementos del kit</p>
            <div className="space-y-1.5">
              {form.kit_items.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={it} onChange={(e) => setKitItem(i, e.target.value)} className="text-black bg-white flex-1" placeholder={`Item ${i+1}`} data-testid={`race-editor-kit-${i}`} />
                  <Button variant="ghost" size="sm" onClick={() => removeKitItem(i)} className="text-red-500"><X size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addKitItem} data-testid="race-editor-kit-add"><UserPlus size={12} className="mr-1" /> Agregar item</Button>
            </div>
          </div>

          {/* Extras */}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Extras al cruzar la meta</p>
            <div className="space-y-1.5">
              {form.meta_extras.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={it} onChange={(e) => setExtra(i, e.target.value)} className="text-black bg-white flex-1" placeholder={`Extra ${i+1}`} data-testid={`race-editor-extra-${i}`} />
                  <Button variant="ghost" size="sm" onClick={() => removeExtra(i)} className="text-red-500"><X size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addExtra} data-testid="race-editor-extra-add"><UserPlus size={12} className="mr-1" /> Agregar extra</Button>
            </div>
          </div>

          {/* Colores */}
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Colores de la carrera</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "primary", label: "Primario" },
                { k: "primary_soft", label: "Suave" },
                { k: "accent", label: "Acento" },
              ].map(({ k, label }) => (
                <label key={k} className="flex items-center gap-2 text-xs">
                  <span className="w-6 h-6 rounded border" style={{ background: form.theme[k] }} />
                  <Input type="color" value={form.theme[k] || "#000000"} onChange={(e) => uTh(k, e.target.value)} className="w-10 h-8 p-0 bg-white" data-testid={`race-editor-color-${k}`} />
                  <span className="text-black text-[10px]">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Sombrero (primeros N) + Deslinde médico */}
          <div className="p-3 rounded border-2 bg-yellow-50" style={{ borderColor: "#F59E0B" }}>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">🎩 Sombrero — primeros N con pago confirmado</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <Field label="Umbral (N)">
                <Input type="number" min="0" value={form.sombrero_threshold ?? 0} onChange={(e) => u("sombrero_threshold", e.target.value)} className="text-black bg-white" data-testid="race-editor-sombrero-threshold" />
              </Field>
              <Field label="Mensaje si alcanzó sombrero" className="sm:col-span-2">
                <Input value={form.sombrero_reached_msg || ""} onChange={(e) => u("sombrero_reached_msg", e.target.value)} className="text-black bg-white" data-testid="race-editor-sombrero-reached" />
              </Field>
              <Field label="Mensaje si NO alcanzó (trae el tuyo)" className="sm:col-span-3">
                <Input value={form.sombrero_missed_msg || ""} onChange={(e) => u("sombrero_missed_msg", e.target.value)} className="text-black bg-white" data-testid="race-editor-sombrero-missed" />
              </Field>
            </div>
            <p className="text-[10px] text-gray-600 mt-1">El contador se activa al confirmar el pago (Stripe o transferencia). Umbral 0 desactiva el sombrero.</p>
          </div>

          <div className="p-3 rounded border-2 bg-red-50" style={{ borderColor: "#DC2626" }}>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">🩺 Deslinde médico (texto que verá el usuario en Step 4)</p>
            <textarea
              value={form.waiver_text || ""}
              onChange={(e) => u("waiver_text", e.target.value)}
              className="w-full min-h-[120px] text-xs bg-white text-black border border-gray-300 rounded p-2 leading-relaxed"
              data-testid="race-editor-waiver-text"
            />
            <p className="text-[10px] text-gray-600 mt-1">Máx 2000 caracteres. El usuario debe aceptar el checkbox para completar la inscripción.</p>
          </div>

          {/* Transferencia */}
          <div className="p-3 rounded border-2 bg-gray-50" style={{ borderColor: form.theme.primary + "40" }}>
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Datos de transferencia (mostrados al usuario)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Field label="Banco"><Input value={form.transfer_info.bank || ""} onChange={(e) => uTi("bank", e.target.value)} className="text-black bg-white" placeholder="BBVA" data-testid="race-editor-bank" /></Field>
              <Field label="Titular"><Input value={form.transfer_info.account_holder || ""} onChange={(e) => uTi("account_holder", e.target.value)} className="text-black bg-white" data-testid="race-editor-holder" /></Field>
              <Field label="Número de cuenta"><Input value={form.transfer_info.account_number || ""} onChange={(e) => uTi("account_number", e.target.value)} className="text-black bg-white font-mono" placeholder="5428780871860813" data-testid="race-editor-account" /></Field>
              <Field label="CLABE (18 dígitos)"><Input value={form.transfer_info.clabe || ""} onChange={(e) => uTi("clabe", e.target.value)} className="text-black bg-white font-mono" data-testid="race-editor-clabe" /></Field>
              <Field label="Nota / referencia" className="sm:col-span-2"><Input value={form.transfer_info.reference_hint || ""} onChange={(e) => uTi("reference_hint", e.target.value)} className="text-black bg-white" placeholder="Poner tu nombre completo como referencia" data-testid="race-editor-refhint" /></Field>
            </div>
          </div>

          {/* Estado */}
          <label className="flex items-center gap-2 text-black cursor-pointer">
            <input type="checkbox" checked={!!form.active} onChange={(e) => u("active", e.target.checked)} data-testid="race-editor-active" />
            <span className="text-sm">Activa (visible en el listado)</span>
          </label>

          {/* Actions */}
          <div className="flex justify-between gap-2 pt-2 border-t border-gray-200">
            <div className="flex gap-2">
              {!isCreate && isAdmin && (
                <>
                  <Button variant="outline" onClick={() => setCloneOpen(true)} className="text-xs" data-testid="race-editor-clone-btn">
                    <ArrowsClockwise size={14} className="mr-1" /> Clonar
                  </Button>
                  <Button variant="outline" onClick={deleteRace} className="text-xs text-red-500 border-red-300" data-testid="race-editor-delete-btn">
                    <Trash size={14} className="mr-1" /> Eliminar
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="text-white font-bold" style={{ background: form.theme.primary }} data-testid="race-editor-save">
                {saving ? "Guardando…" : (isCreate ? "Crear carrera" : "Guardar cambios")}
              </Button>
            </div>
          </div>
        </div>
        {!isCreate && <CloneRaceDialog race={race} open={cloneOpen} onClose={() => setCloneOpen(false)} onSaved={() => { setCloneOpen(false); onSaved?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function CloneRaceDialog({ race, open, onClose, onSaved }) {
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && race) { setNewName(`${race.name} — Nueva edición`); setNewDate(""); } }, [open, race]);
  const clone = async () => {
    if (!newName.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      await api.post(`/races/${race.id}/clone`, { name: newName.trim(), event_date: newDate });
      toast.success("Carrera clonada");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white text-black" data-testid="race-clone-dialog">
        <DialogHeader><DialogTitle className="text-black">Clonar "{race?.name}"</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-black/70">Crea una carrera nueva copiando todos los datos (kit, colores, transferencia, etc.). Personaliza nombre y fecha.</p>
          <Field label="Nombre de la nueva edición"><Input value={newName} onChange={(e) => setNewName(e.target.value)} className="text-black bg-white" data-testid="race-clone-name" /></Field>
          <Field label="Nueva fecha (YYYY-MM-DD)"><Input value={newDate} onChange={(e) => setNewDate(e.target.value)} placeholder="2027-09-14" className="text-black bg-white" data-testid="race-clone-date" /></Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={clone} disabled={saving} className="bg-black text-white font-bold" data-testid="race-clone-confirm">
              {saving ? "Clonando…" : "Clonar carrera"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// MANUAL REGISTRATION DIALOG — admin inscribe corredores a mano
// (uno por uno, carga masiva pegada, o OCR por foto)
// ============================================================
const PAYMENT_STATUSES = [
  { value: "pending", label: "Pendiente" },
  { value: "paid", label: "Pagado" },
  { value: "pending_transfer", label: "Transferencia" },
  { value: "awaiting_review", label: "Revisar comprobante" },
];
const CATS = ["", "6", "5", "4", "3", "2", "1", "open", "A", "B", "C", "D"];

function emptyEntry() {
  return {
    full_name: "", email: "", phone: "", shirt_size: "", gender: "F",
    category: "", age: "", payment_status: "pending",
  };
}

function ManualRegistrationDialog({ raceId, race, open, onClose, onDone, theme }) {
  const [tab, setTab] = useState("single"); // single | bulk | photo
  const [entries, setEntries] = useState([emptyEntry()]);
  const [bulkText, setBulkText] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const t = theme || race?.theme || {};
  const sizes = race?.shirt_sizes || SIZES;

  const reset = () => {
    setEntries([emptyEntry()]);
    setBulkText("");
    setPhotoFile(null);
    setResult(null);
  };

  const update = (i, field, value) => {
    setEntries((prev) => prev.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));
  };
  const addRow = () => setEntries((prev) => [...prev, emptyEntry()]);
  const removeRow = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const parseBulk = () => {
    // Accept CSV / tab-separated. Lines:
    // full_name, email, phone, shirt_size, gender(F|M), category, age, payment_status
    const lines = bulkText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((line) => {
      const sep = line.includes("\t") ? "\t" : (line.includes(";") ? ";" : ",");
      const cols = line.split(sep).map((c) => c.trim());
      return {
        full_name: cols[0] || "",
        email: (cols[1] || "").toLowerCase(),
        phone: cols[2] || "",
        shirt_size: (cols[3] || "").toUpperCase(),
        gender: (cols[4] || "F").toUpperCase().slice(0, 1),
        category: cols[5] || "",
        age: cols[6] || "",
        payment_status: cols[7] || "pending",
      };
    }).filter((e) => e.full_name);
    if (!parsed.length) {
      toast.error("No se detectaron corredores (línea 1: Nombre,Email,Teléfono,Talla,Género,Categoría,Edad,Pago)");
      return;
    }
    setEntries(parsed);
    setTab("single");
    toast.success(`${parsed.length} corredores cargados — revisa y guarda`);
  };

  const runOcr = async () => {
    if (!photoFile) { toast.error("Selecciona una imagen"); return; }
    setOcrLoading(true);
    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve((reader.result || "").toString().split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(photoFile);
      });
      const { data } = await api.post(`/races/${raceId}/manual-register/photo`, { image_base64: b64 });
      if (!data?.entries?.length) {
        toast.error("No se extrajeron corredores de la imagen");
      } else {
        setEntries(data.entries.map((e) => ({ ...emptyEntry(), ...e })));
        setTab("single");
        toast.success(`${data.entries.length} corredores extraídos de la imagen — revisa y guarda`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const submit = async () => {
    const payload = entries
      .map((e) => ({ ...e, age: e.age ? Number(e.age) : null, category: e.category || null }))
      .filter((e) => e.full_name && (e.email || e.phone));
    if (!payload.length) {
      toast.error("Agrega al menos un corredor con nombre y email/teléfono");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/races/${raceId}/manual-register`, { entries: payload });
      setResult(data);
      toast.success(`Procesados: ${data.total} · Creados: ${data.created} · Enlazados: ${data.linked}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-hidden flex flex-col text-gray-900" data-testid="race-manual-dialog">
        <DialogHeader>
          <DialogTitle style={{ color: t.primary }}>Inscribir corredores manualmente</DialogTitle>
          <DialogDescription>
            Crea cuentas nuevas o enlaza las existentes. Las contraseñas temporales aparecerán en el PDF para entrega física.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[
            { id: "single", label: "Uno por uno", icon: UserPlus },
            { id: "bulk", label: "Carga masiva (pegar)", icon: UploadSimple },
            { id: "photo", label: "Por foto/imagen", icon: Camera },
          ].map((x) => (
            <button
              key={x.id}
              onClick={() => setTab(x.id)}
              className={`px-3 py-2 text-xs uppercase font-bold flex items-center gap-1 border-b-2 ${tab === x.id ? "" : "border-transparent text-gray-500"}`}
              style={tab === x.id ? { borderColor: t.primary, color: t.primary } : {}}
              data-testid={`race-manual-tab-${x.id}`}
            >
              <x.icon size={14} /> {x.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-3">
          {tab === "bulk" && (
            <div className="space-y-2">
              <p className="text-xs text-gray-600">
                Pega filas con el formato <code>Nombre, Email, Teléfono, Talla, Género(F|M), Categoría, Edad, Pago</code>.
                Separador: coma, punto y coma o tabulación. Una fila por corredor.
              </p>
              <Textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={10}
                placeholder={"Juan Perez, juan@mail.com, 5215512345678, M, M, 3, 32, paid\nMaria Lopez, maria@mail.com, 5215598765432, S, F, open, , pending"}
                className="text-xs font-mono text-gray-900 bg-white"
                data-testid="race-manual-bulk-text"
              />
              <Button onClick={parseBulk} className="text-white uppercase text-xs" style={{ background: t.primary }} data-testid="race-manual-bulk-parse">
                <ArrowRight size={14} className="mr-1" /> Cargar y revisar
              </Button>
            </div>
          )}

          {tab === "photo" && (
            <div className="space-y-3">
              <p className="text-xs text-gray-600">
                Sube una foto (manuscrita, captura de WhatsApp, lista impresa). El sistema extraerá los corredores
                y los pasará a la vista de revisión para que confirmes y guardes.
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="race-manual-photo-input"
              />
              {photoFile && <p className="text-xs text-gray-500">{photoFile.name} · {(photoFile.size / 1024).toFixed(0)} KB</p>}
              <Button onClick={runOcr} disabled={!photoFile || ocrLoading} className="text-white uppercase text-xs" style={{ background: t.primary }} data-testid="race-manual-photo-ocr">
                <Camera size={14} className="mr-1" />
                {ocrLoading ? "Analizando imagen…" : "Extraer corredores con IA"}
              </Button>
            </div>
          )}

          {tab === "single" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  {entries.length} corredores. Email o teléfono es obligatorio para crear/enlazar la cuenta.
                </p>
                <Button size="sm" variant="outline" onClick={addRow} data-testid="race-manual-add-row">
                  <Plus size={12} className="mr-1" /> Agregar fila
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left uppercase text-[10px]" style={{ background: t.primary_soft, color: t.primary }}>
                    <tr>
                      <th className="p-2">#</th>
                      <th>Nombre *</th>
                      <th>Email</th>
                      <th>Teléfono / WA</th>
                      <th>Talla</th>
                      <th>Género</th>
                      <th>Categoría</th>
                      <th>Edad</th>
                      <th>Pago</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i} className="border-b border-gray-100 align-top" data-testid={`race-manual-row-${i}`}>
                        <td className="p-1 text-gray-400">{i + 1}</td>
                        <td className="p-1"><Input value={e.full_name} onChange={(ev) => update(i, "full_name", ev.target.value)} className="h-8 text-xs text-gray-900 bg-white" data-testid={`race-manual-name-${i}`} /></td>
                        <td className="p-1"><Input type="email" value={e.email} onChange={(ev) => update(i, "email", ev.target.value)} className="h-8 text-xs text-gray-900 bg-white" data-testid={`race-manual-email-${i}`} /></td>
                        <td className="p-1"><Input value={e.phone} onChange={(ev) => update(i, "phone", ev.target.value)} className="h-8 text-xs text-gray-900 bg-white" data-testid={`race-manual-phone-${i}`} /></td>
                        <td className="p-1">
                          <select value={e.shirt_size} onChange={(ev) => update(i, "shirt_size", ev.target.value)} className="h-8 border rounded px-1 text-xs text-gray-900 bg-white" data-testid={`race-manual-size-${i}`}>
                            <option value="">—</option>
                            {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="p-1">
                          <select value={e.gender} onChange={(ev) => update(i, "gender", ev.target.value)} className="h-8 border rounded px-1 text-xs text-gray-900 bg-white" data-testid={`race-manual-gender-${i}`}>
                            <option value="F">Mujer</option>
                            <option value="M">Hombre</option>
                          </select>
                        </td>
                        <td className="p-1">
                          <select value={e.category} onChange={(ev) => update(i, "category", ev.target.value)} className="h-8 border rounded px-1 text-xs text-gray-900 bg-white" data-testid={`race-manual-cat-${i}`}>
                            {CATS.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                          </select>
                        </td>
                        <td className="p-1 w-16"><Input type="number" value={e.age} onChange={(ev) => update(i, "age", ev.target.value)} className="h-8 text-xs w-16 text-gray-900 bg-white" data-testid={`race-manual-age-${i}`} /></td>
                        <td className="p-1">
                          <select value={e.payment_status} onChange={(ev) => update(i, "payment_status", ev.target.value)} className="h-8 border rounded px-1 text-xs text-gray-900 bg-white" data-testid={`race-manual-pay-${i}`}>
                            {PAYMENT_STATUSES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        </td>
                        <td className="p-1">
                          {entries.length > 1 && (
                            <button onClick={() => removeRow(i)} className="text-red-500" data-testid={`race-manual-remove-${i}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="mt-4 rounded border p-3 bg-gray-50 text-xs" data-testid="race-manual-result">
              <p className="font-bold mb-2" style={{ color: t.primary }}>
                Resultado: {result.total} procesados · {result.created} creados · {result.linked} enlazados · {result.failed} fallidos
              </p>
              <div className="max-h-48 overflow-y-auto">
                {result.results.map((r, i) => (
                  <div key={i} className={`py-1 border-b ${r.ok ? "" : "text-red-600"}`}>
                    {r.ok ? (
                      <>
                        <span className="font-mono">{r.bib_number || "—"}</span> · {r.name}
                        {" · "}<span className="uppercase text-[10px] px-1 rounded" style={{ background: r.account_action === "created" ? "#DCFCE7" : "#DBEAFE" }}>{r.account_action}</span>
                        {r.temp_password && <> · Contraseña temporal: <code className="font-bold">{r.temp_password}</code></>}
                      </>
                    ) : (
                      <>Error: {r.error} — {r.input?.full_name || r.input?.email}</>
                    )}
                  </div>
                ))}
              </div>
              <a
                href={`${BACKEND_URL}/api/races/${raceId}/credentials.pdf?scope=manual`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center mt-2 text-xs font-bold hover:underline"
                style={{ color: t.primary }}
                data-testid="race-manual-pdf-link"
              >
                <FilePdf size={14} className="mr-1" /> Descargar PDF de credenciales (manuales)
              </a>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center gap-2 pt-2 border-t">
          <Button variant="outline" onClick={handleClose} data-testid="race-manual-close">
            Cerrar
          </Button>
          {tab === "single" && (
            <Button onClick={submit} disabled={saving} className="text-white font-bold uppercase" style={{ background: t.primary }} data-testid="race-manual-submit">
              {saving ? "Guardando…" : `Registrar ${entries.filter((e) => e.full_name).length} corredores`}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
